import { forwardRef } from 'react';
import classNames from 'classnames';
import { OpacityType } from '@vds/core';
import { UI, BG, Brand, Accent } from '@vds/core';
import './Box.scss';

type OverlayVariant =
  | 'full'
  | 'half'
  | 'quarter'
  | 'nav'
  | 'user-profile'
  | 'thumbnail'
  | 'on-now'
  | 'video-player'
  | 'marquee-desktop'
  | 'marquee-mobile'
  | 'detail'
  | 'endcard-show'
  | 'endcard-movie'
  | 'endcard-live'
  | 'scrim-desktop'
  | 'scrim-mobile'
  | 'solid'
  | 'content-locked';

export interface BoxProps {
  component?: React.ElementType;
  className?: string;
  children?: React.ReactNode;
  styles?: React.CSSProperties;
  width?: React.CSSProperties['width'];
  height?: React.CSSProperties['height'];
  bgColor?: UI | BG | Brand | Accent;
  overlayVariant?: OverlayVariant;
  overlayOpacity?: OpacityType;
}

/**
 * Box Component
 * @param styles - The system prop that allows defining system overrides as well as additional CSS styles.
 * @param component - The component used for the root node. Either a string to use a HTML element or a component.
 * @param className - Additional classNames for the component.
 * @param children - Any React.Node Element.
 * @param width - Sets the width.
 * @param height - Sets the height.
 * @param bgColor - Sets the color background for box using design tokens.
 * @param overlayVariant - Setting to configure the overlay for the box.
 * @param overlayOpacity - Setting to configure the overlay for the box.
 */
export const Box = forwardRef<React.ReactNode, BoxProps>(
  (
    {
      component: Component = 'div',
      className,
      children,
      width,
      height,
      bgColor,
      overlayVariant,
      overlayOpacity,
      styles = {},
      ...rest
    },
    ref,
  ) => {
    return (
      <Component
        className={classNames('vds-box', className)}
        ref={ref}
        style={{ ...styles, backgroundColor: `var(${bgColor})`, height, width }}
        {...rest}>
        {overlayVariant && (
          <div
            style={{ opacity: `var(--${overlayOpacity ?? 'opacity-100'})` }}
            className={classNames('vds-box__overlay', {
              [`vds-box__overlay--${overlayVariant}`]: overlayVariant,
            })}
          />
        )}
        {children}
      </Component>
    );
  },
);
